/**
 * Public Credit Registry Page — /credit/:creditId
 * No auth required. Immutable ledger view with full event history.
 */
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, X, Leaf, AlertTriangle, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL    = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const STATUS_CFG = {
  issued:  { color:"#059669", bg:"bg-emerald-50",  border:"border-emerald-200", label:"Issued — Active" },
  listed:  { color:"#3B82F6", bg:"bg-blue-50",     border:"border-blue-200",    label:"Listed on Marketplace" },
  sold:    { color:"#6366F1", bg:"bg-indigo-50",   border:"border-indigo-200",  label:"Sold" },
  retired: { color:"#94A3B8", bg:"bg-slate-100",   border:"border-slate-200",   label:"Permanently Retired" },
};
const EVENT_LABELS = {
  issued:    "Credit Issued",
  transferred:"Transferred",
  listed:    "Listed on Marketplace",
  purchased: "Purchased",
  retired:   "Permanently Retired",
};

export default function CreditRegistryPage() {
  const { creditId } = useParams();
  const [credit,  setCredit]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/credits/${creditId}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? "Credit not found" : "Failed to load");
        return r.json();
      })
      .then(d => { setCredit(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [creditId]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !credit) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center p-8">
        <AlertTriangle size={32} className="mx-auto text-amber-400 mb-3" />
        <h2 className="text-lg font-bold text-[#0F172A] mb-2">Credit Not Found</h2>
        <p className="text-sm text-slate-500 mb-4">{error}</p>
        <Link to="/" className="text-sm text-[#059669] hover:underline">← Return to DeCarbonIQ</Link>
      </div>
    </div>
  );

  const statusCfg  = STATUS_CFG[credit.status] || STATUS_CFG.issued;
  const isRetired  = credit.status === "retired";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="credit-registry-page">
      <nav className="bg-[#0F172A] h-14 flex items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{filter:"brightness(0) invert(1)"}} />
          <span className="text-xs text-slate-500 hidden sm:block uppercase tracking-widest">Credit Registry</span>
        </div>
        <span className="text-xs text-slate-500 font-mono hidden md:block">{creditId}</span>
      </nav>

      <div className="px-4 py-10">
        {/* Status banner */}
        <div className={`rounded-xl border-2 p-6 text-center mb-6 ${isRetired ? "border-slate-200 bg-slate-50" : "border-emerald-300 bg-emerald-50"}`}
          data-testid="credit-status-banner">
          {isRetired
            ? <X size={40} className="mx-auto text-slate-400 mb-2" />
            : <CheckCircle2 size={40} className="mx-auto text-[#059669] mb-2" />}
          <p className={`text-xl font-bold ${isRetired ? "text-slate-600" : "text-[#0F172A]"}`}>{statusCfg.label}</p>
          {isRetired && (
            <p className="text-xs text-slate-400 mt-1">This credit has been permanently retired as a carbon offset. It cannot be reused.</p>
          )}
        </div>

        {/* Credit Identity */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
          <div className="bg-[#0F172A] px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Leaf size={18} className="text-[#059669]" />
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Carbon Credit</p>
              <p className="text-base font-bold font-mono text-white" data-testid="credit-id">{credit.credit_id}</p>
            </div>
          </div>
          <div className="p-5 space-y-2">
            {[
              ["Owner",           credit.owner_name],
              ["Volume",          `${credit.volume_tco2e?.toFixed(4)} tCO₂e`],
              ["Vintage Year",    credit.vintage_year],
              ["Serial Range",    credit.serial_range],
              ["Methodology",     `${credit.methodology?.id} — ${credit.methodology?.name}`],
              ["Verification",    credit.verification_level?.replace(/_/g," ")],
              ["Status",          statusCfg.label],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-xs text-slate-400">{label}</span>
                <span className="text-xs font-semibold text-[#0F172A] capitalize">{String(value || "—")}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Retirement info */}
        {isRetired && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Retirement Record</p>
            {[["Retired by", credit.retired_by], ["Retired on", credit.retired_at?.slice(0,10)],
              ["Reason", credit.retirement_reason]].filter(([,v])=>v).map(([l,v])=>(
              <div key={l} className="flex justify-between py-1 text-xs">
                <span className="text-slate-400">{l}</span>
                <span className="font-medium text-slate-700">{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Registry Events */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="registry-events">
          <p className="text-sm font-semibold text-slate-900 mb-4">Registry Event Log</p>
          <div className="space-y-3">
            {(credit.registry_events || []).map((e, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-[#059669] border-2 border-emerald-100 flex-shrink-0 mt-0.5" />
                  {i < (credit.registry_events.length - 1) && (
                    <div className="w-0.5 h-full bg-slate-100 mt-1 mb-1" />
                  )}
                </div>
                <div className="flex-1 pb-3">
                  <div className="flex justify-between mb-0.5">
                    <p className="text-xs font-semibold text-[#0F172A]">{EVENT_LABELS[e.event] || e.event}</p>
                    <span className="text-[10px] text-slate-400">{e.timestamp?.slice(0,16).replace("T"," ")}</span>
                  </div>
                  <p className="text-xs text-slate-500">{e.from} → {e.to}</p>
                  {e.notes && <p className="text-xs text-slate-400 mt-0.5 italic">"{e.notes.slice(0,120)}"</p>}
                  {e.tx_hash && (
                    <p className="text-[10px] font-mono text-slate-300 mt-0.5 break-all">
                      TX: {e.tx_hash}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Methodology + Integrity */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Data Integrity</p>
          <div className="space-y-1.5">
            {[
              `GHG Protocol Corporate Standard — ${credit.methodology?.version || "v1.0"}`,
              `Emission reduction basis: ${credit.reduction_basis_tco2e?.toFixed(2)} tCO₂e × ${credit.eligibility_factor} eligibility factor`,
              `Credits issued: ${credit.volume_tco2e?.toFixed(4)} tCO₂e`,
              `Serially numbered — no double counting`,
            ].map((line, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <CheckCircle2 size={10} className="text-emerald-500 flex-shrink-0" />
                <span className="text-slate-500">{line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <Link to="/marketplace" className="text-sm text-[#059669] hover:underline flex items-center gap-1 justify-center">
            <ExternalLink size={13} /> View Carbon Credit Marketplace
          </Link>
          <p className="text-xs text-slate-400 mt-2">
            This record is maintained by DeCarbonIQ DPCS+ Registry. All transactions are immutable.
          </p>
        </div>
      </div>
    </div>
  );
}
